import {
  Alert,
  alpha,
  Box,
  Button,
  Chip,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SearchRounded from "@mui/icons-material/SearchRounded";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { useEffect, useRef, useState, type FormEvent } from "react";
import {
  formatTournamentDateRange,
  formatTournamentFacilityLine,
  getTournamentCommerceLabel,
} from "@bw/shared/utils";
import type { BrowseTournamentResponse } from "@bw/shared/types/generated";
import { Page } from "../../../common/components/Page";
import { formatDistance, readRadiusKm } from "../../../common/distanceUnits";
import { useDistanceUnits } from "../../../common/hooks/useDistanceUnits";
import { useAuth } from "@bw/shared/hooks";
import { useMyProfile } from "../../../common/query/useMyProfile";
import { useNearbyCenter } from "../../../common/hooks/useNearbyCenter";
import {
  clearDiscoveryCenter,
  readDiscoveryCenter,
  writeDiscoveryCenter,
} from "../../../common/discoveryLocation";
import { TournamentDiscoveryFilters } from "../components/TournamentDiscoveryFilters";
import { OfficialBadge } from "../components/OfficialBadge";
import { useTournaments } from "../hooks/useTournaments";
import { visuallyHidden } from "../../../common/visuallyHidden";

/**
 * TournamentsListPage (/tournaments) — the player's tournament discovery list,
 * reached THROUGH the Play hub (no new top-level nav, mirroring booking /
 * drop-in / classes / coaches). Reads the browse page (GET /tournaments →
 * {items, total, limit, offset}, anonymous-readable) and renders one dense,
 * scannable TP-style result row per tournament (see TournamentRow).
 *
 * Each row shows ONLY BrowseTournamentResponse-backed facts: the name, a single
 * meta line carrying the facility (name + city/state) AND the date range, the
 * lifecycle/registration state as compact badges, and the entry-fee summary (all
 * via the shared tournament helpers — never a client-computed string). The whole
 * row LINKS to the detail (/tournaments/{id}) — the uuid is the link target,
 * never visible body text. No participant/entry COUNT is invented (the browse
 * item carries none).
 *
 * A status filter (All / Registration open / Upcoming / Completed) RE-QUERIES
 * browse with the chosen REAL status value, resetting to the first page.
 * Pagination advances the offset by the page size off the REAL `total` — never a
 * fabricated count. Honesty: loading is role="status", error is role="alert"
 * (gated on react-query isError, NOT !data — a failed fetch is NOT the empty
 * state), and the empty state is "No tournaments".
 *
 * The AppShell supplies no page title, so this routed page OWNS its single <h1>
 * via <Page title="Tournaments">.
 */

const PAGE_SIZE = 20;

function option<T extends string>(
  value: string | null,
  choices: readonly T[],
): T | undefined {
  return choices.find((choice) => choice === value);
}

/**
 * The status filter. `label` is the concise VISIBLE chip text; `name` is the
 * canonical accessible name (the filter's meaning, used as `aria-label`); `value`
 * is the REAL backend status forwarded to browse (undefined = no filter).
 *
 * The "Accepting entries" chip's accessible name is "Registration open" — the
 * concise visible word deliberately avoids re-using the word "open", which a
 * tournament's own name ("Spring Open") may also contain, while the aria-label
 * keeps the control's meaning unambiguous to assistive tech.
 */
const STATUS_FILTERS: {
  label: string;
  name: string;
  value: string | undefined;
}[] = [
  { label: "All", name: "All tournaments", value: undefined },
  {
    label: "Accepting entries",
    name: "Registration open",
    value: "registration_open",
  },
  { label: "Upcoming", name: "Upcoming", value: "upcoming" },
  { label: "Ongoing", name: "Ongoing", value: "in_progress" },
  { label: "Completed", name: "Completed", value: "completed" },
];

export function TournamentsListPage() {
  const [params, setParams] = useSearchParams();
  const { user, loading: authLoading } = useAuth();
  const { center, setCenter, clearCenter } = useNearbyCenter();
  const { data: profile, isSuccess: profileLoaded } = useMyProfile(
    Boolean(user),
  );
  const coordinateSearchAllowed =
    !authLoading &&
    (user === null ||
      (profileLoaded && profile?.requires_parental_consent === false));
  const { measurementSystem } = useDistanceUnits();
  const committedQuery = params.get("q") ?? "";
  const [queryInput, setQueryInput] = useState(committedQuery);
  const lastAutoCommit = useRef<string | null>(null);
  useEffect(() => {
    if (lastAutoCommit.current === committedQuery) {
      lastAutoCommit.current = null;
      return;
    }
    setQueryInput(committedQuery);
  }, [committedQuery]);
  useEffect(() => {
    const query = queryInput.trim();
    if (query === committedQuery || query.length === 1) return;
    const timer = window.setTimeout(() => {
      lastAutoCommit.current = query;
      setParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          if (query) next.set("q", query);
          else next.delete("q");
          next.delete("page");
          return next;
        },
        { replace: true },
      );
    }, 330);
    return () => window.clearTimeout(timer);
  }, [committedQuery, queryInput, setParams]);
  const facilityId = params.get("facility_id") || undefined;
  const urlCenter = readDiscoveryCenter(params);
  const discoveryCenter = coordinateSearchAllowed
    ? (urlCenter ?? center)
    : null;
  const city = discoveryCenter
    ? undefined
    : params.get("city")?.trim() || undefined;
  const locationLabel = discoveryCenter?.label;
  const radiusKm = readRadiusKm(params.get("radius_km"), measurementSystem);
  const startsAfter = /^\d{4}-\d{2}-\d{2}$/.test(
    params.get("starts_after") ?? "",
  )
    ? (params.get("starts_after") ?? undefined)
    : undefined;
  const tournamentType = option(params.get("tournament_type"), [
    "individual",
    "team",
  ] as const);
  const eventMode = option(params.get("event_mode"), [
    "singles",
    "doubles",
    "mixed_doubles",
  ] as const);
  const eventGrade = option(params.get("event_grade"), [
    "A",
    "B",
    "C",
    "D",
    "J",
  ] as const);
  const officialOnly = params.get("official") === "true";
  const requestedStatus = params.get("status");
  const status =
    requestedStatus === "all"
      ? undefined
      : (STATUS_FILTERS.find((filter) => filter.value === requestedStatus)
          ?.value ?? "upcoming");
  const rawPage = Number(params.get("page"));
  const page = Number.isSafeInteger(rawPage) && rawPage > 0 ? rawPage : 1;
  const offset = (page - 1) * PAGE_SIZE;
  function setOffset(value: number) {
    setParams((previous) => {
      const next = new URLSearchParams(previous);
      if (value) next.set("page", String(value / PAGE_SIZE + 1));
      else next.delete("page");
      return next;
    });
  }

  const { data, isLoading, isError } = useTournaments({
    q: committedQuery.trim() || undefined,
    status,
    tournament_type: tournamentType,
    event_mode: eventMode,
    event_grade: eventGrade,
    official: officialOnly,
    city,
    facility_id: facilityId,
    starts_after: startsAfter,
    ...(discoveryCenter
      ? {
          lat: discoveryCenter.latitude,
          lng: discoveryCenter.longitude,
          radius_km: radiusKm,
        }
      : {}),
    limit: PAGE_SIZE,
    offset,
  });

  const tournaments = data?.items ?? [];
  const total = data?.total ?? 0;
  const hasNext = offset + PAGE_SIZE < total;
  const hasPrev = offset > 0;
  const resultNoun = `${total} ${total === 1 ? "tournament" : "tournaments"}${
    committedQuery.trim() ? ` matching “${committedQuery.trim()}”` : ""
  }`;
  const resultHeading = discoveryCenter
    ? `${resultNoun} within ${
        measurementSystem === "metric"
          ? `${Number(radiusKm.toFixed(1))} km`
          : `${Number((radiusKm / 1.609344).toFixed(1))} mi`
      } of ${locationLabel || "your location"}`
    : city
      ? `${resultNoun} in ${city}`
      : resultNoun;

  function selectStatus(value: string | undefined) {
    setParams((previous) => {
      const next = new URLSearchParams(previous);
      if (value) next.set("status", value);
      else next.delete("status");
      next.delete("page");
      return next;
    });
  }

  function submitSearch(event: FormEvent) {
    event.preventDefault();
    const query = queryInput.trim();
    setParams((previous) => {
      const next = new URLSearchParams(previous);
      if (query) next.set("q", query);
      else next.delete("q");
      next.delete("page");
      return next;
    });
  }

  let body: React.ReactNode;
  if (isLoading) {
    body = (
      <Typography color="text.secondary" role="status">
        Loading tournaments…
      </Typography>
    );
  } else if (isError) {
    body = (
      <Alert severity="error" role="alert">
        We couldn&apos;t load tournaments. Please try again.
      </Alert>
    );
  } else if (tournaments.length === 0 && officialOnly) {
    body = (
      <Stack spacing={1} sx={{ alignItems: "flex-start" }}>
        <Typography color="text.secondary">
          No official BW tournaments match these filters yet. Official
          tournaments are sanctioned by Badminton Warriors and count toward
          your BW rating.
        </Typography>
        <Button
          size="small"
          variant="outlined"
          onClick={() =>
            setParams((previous) => {
              const next = new URLSearchParams(previous);
              next.delete("official");
              next.delete("page");
              return next;
            })
          }
        >
          Show all tournaments
        </Button>
      </Stack>
    );
  } else if (tournaments.length === 0) {
    body = <Typography color="text.secondary">No tournaments</Typography>;
  } else {
    body = (
      <>
        <List disablePadding component={Paper} variant="outlined" role="list">
          {tournaments.map((t) => (
            <TournamentRow
              key={t.tournament_id}
              tournament={t}
              facilityFilter={facilityId}
              measurementSystem={measurementSystem}
            />
          ))}
        </List>
        {(hasPrev || hasNext) && (
          <Stack
            component="nav"
            direction="row"
            spacing={1}
            aria-label="Tournament pages"
            sx={{ justifyContent: "space-between" }}
          >
            <Button
              type="button"
              variant="outlined"
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              disabled={!hasPrev}
            >
              Previous
            </Button>
            <Button
              type="button"
              variant="outlined"
              onClick={() => setOffset(offset + PAGE_SIZE)}
              disabled={!hasNext}
            >
              Next
            </Button>
          </Stack>
        )}
      </>
    );
  }

  return (
    <Page>
      <Box
        sx={{
          display: { xs: "flex", md: "grid" },
          flexDirection: "column",
          gridTemplateColumns: { md: "280px minmax(0, 1fr)" },
          columnGap: { md: 3 },
          rowGap: 2,
          minWidth: 0,
        }}
      >
        <Typography component="h1" sx={visuallyHidden}>
          Tournaments
        </Typography>
        <Typography
          color="text.secondary"
          sx={{ gridColumn: { md: 2 }, maxWidth: 720 }}
        >
          Find upcoming badminton events by location, date, and entry status.
          Select a tournament for events, draws, entrants, schedule, and
          results.
        </Typography>
        <Box
          component="form"
          role="search"
          aria-label="Search tournaments"
          onSubmit={submitSearch}
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 1,
            gridColumn: { md: 2 },
            maxWidth: 720,
          }}
        >
          <TextField
            fullWidth
            label="Tournament or event"
            placeholder="Search by tournament, event, or venue name"
            value={queryInput}
            onChange={(event) => setQueryInput(event.target.value)}
            autoComplete="off"
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRounded aria-hidden="true" />
                  </InputAdornment>
                ),
              },
            }}
          />
          <Button
            type="submit"
            variant="contained"
            sx={{ px: { xs: 2, sm: 3 }, width: { xs: "100%", sm: "auto" } }}
          >
            Search
          </Button>
        </Box>
        <Box
          component="aside"
          aria-label="Tournament filters"
          sx={{
            gridColumn: { md: 1 },
            gridRow: { md: "1 / span 7" },
            alignSelf: { md: "start" },
            position: { md: "sticky" },
            top: { md: 88 },
            p: { md: 2 },
            border: { md: 1 },
            borderColor: { md: "divider" },
            borderRadius: { md: 2 },
            bgcolor: { md: "background.paper" },
            maxHeight: { md: "calc(100dvh - 112px)" },
            overflowY: { md: "auto" },
            minWidth: 0,
          }}
        >
          <Box sx={{ display: { xs: "none", md: "block" }, mb: 2 }}>
            <Typography component="h2" variant="h6" sx={{ fontWeight: 800 }}>
              Filter tournaments
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Narrow by location, date, event type, format, or grade.
            </Typography>
          </Box>
          <TournamentDiscoveryFilters
            desktopExpanded
            facilityId={facilityId}
            city={city}
            startsAfter={startsAfter}
            tournamentType={tournamentType}
            eventMode={eventMode}
            eventGrade={eventGrade}
            officialOnly={officialOnly}
            locationLabel={locationLabel}
            latitude={discoveryCenter?.latitude}
            longitude={discoveryCenter?.longitude}
            radiusKm={radiusKm}
            measurementSystem={measurementSystem}
            coordinateSearchAllowed={coordinateSearchAllowed}
            onFacilityChange={(value) => {
              if (value) clearCenter();
              setParams((previous) => {
                const next = value
                  ? clearDiscoveryCenter(previous)
                  : new URLSearchParams(previous);
                if (value) next.delete("city");
                if (value) next.set("facility_id", value);
                else next.delete("facility_id");
                next.delete("page");
                return next;
              });
            }}
            onCityChange={(value) => {
              if (value) clearCenter();
              setParams((previous) => {
                const next = value
                  ? clearDiscoveryCenter(previous)
                  : new URLSearchParams(previous);
                if (value) next.delete("facility_id");
                if (value) next.set("city", value);
                else next.delete("city");
                next.delete("page");
                return next;
              });
            }}
            onStartsAfterChange={(value) =>
              setParams((previous) => {
                const next = new URLSearchParams(previous);
                if (value) next.set("starts_after", value);
                else next.delete("starts_after");
                next.delete("page");
                return next;
              })
            }
            onTournamentTypeChange={(value) =>
              setParams((previous) => {
                const next = new URLSearchParams(previous);
                if (value) next.set("tournament_type", value);
                else next.delete("tournament_type");
                next.delete("page");
                return next;
              })
            }
            onEventModeChange={(value) =>
              setParams((previous) => {
                const next = new URLSearchParams(previous);
                if (value) next.set("event_mode", value);
                else next.delete("event_mode");
                next.delete("page");
                return next;
              })
            }
            onEventGradeChange={(value) =>
              setParams((previous) => {
                const next = new URLSearchParams(previous);
                if (value) next.set("event_grade", value);
                else next.delete("event_grade");
                next.delete("page");
                return next;
              })
            }
            onOfficialOnlyChange={(value) =>
              setParams((previous) => {
                const next = new URLSearchParams(previous);
                if (value) next.set("official", "true");
                else next.delete("official");
                next.delete("page");
                return next;
              })
            }
            onLocationChange={(nextCenter) => {
              setCenter(nextCenter);
              setParams((previous) => {
                const next = writeDiscoveryCenter(
                  previous,
                  nextCenter,
                  radiusKm,
                );
                next.delete("city");
                next.delete("facility_id");
                return next;
              });
            }}
            onRadiusChange={(value) =>
              setParams((previous) => {
                const next = new URLSearchParams(previous);
                next.set("radius_km", String(value));
                next.delete("page");
                return next;
              })
            }
            onClearLocation={() => {
              clearCenter();
              setParams((previous) => {
                return clearDiscoveryCenter(previous);
              });
            }}
            onClearAll={() => {
              clearCenter();
              setParams((previous) => {
                const next = new URLSearchParams(previous);
                for (const key of [
                  "facility_id",
                  "city",
                  "starts_after",
                  "tournament_type",
                  "event_mode",
                  "event_grade",
                  "official",
                  "lat",
                  "lng",
                  "location_label",
                  "radius_km",
                  "page",
                ])
                  next.delete(key);
                return next;
              });
            }}
          />
          <TextField
            select
            fullWidth
            size="small"
            label="Status"
            value={status ?? "all"}
            onChange={(event) =>
              selectStatus(
                event.target.value === "all" ? "all" : event.target.value,
              )
            }
            sx={{ mt: 2 }}
          >
            {STATUS_FILTERS.map((filter) => (
              <MenuItem key={filter.name} value={filter.value ?? "all"}>
                {filter.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        {!isLoading && !isError ? (
          <Typography
            component="h2"
            variant="subtitle1"
            aria-live="polite"
            sx={{ gridColumn: { md: 2 }, fontWeight: 800, minWidth: 0 }}
          >
            {resultHeading}
          </Typography>
        ) : null}

        <Box sx={{ gridColumn: { md: 2 }, minWidth: 0 }}>{body}</Box>
      </Box>
    </Page>
  );
}

/**
 * Concise, YEAR-FREE registration-state phrasing for the card, mapped from the
 * BACKEND's authoritative `lifecycle.registration_state` ("scheduled" | "open" |
 * "closed"). The frontend NEVER re-derives open/closed from the raw
 * registration_*_at timestamps — the backend forces "closed" for completed /
 * archived / cancelled events even when the close timestamp is still in the
 * future, and the player must see THAT truth (not a date-only guess that would
 * tell them entries are open when registration will be rejected).
 *
 * When `lifecycle` is absent we do NOT fabricate an open/closed state — a neutral
 * honest placeholder is shown instead.
 */
const REGISTRATION_STATE_CARD_LABELS: Record<string, string> = {
  open: "Accepting entries",
  scheduled: "Entries open soon",
  closed: "Entries closed",
};
const REGISTRATION_STATE_FALLBACK = "Entries to be announced";

/**
 * Lifecycle phases whose `current_phase_label` merely RESTATES the registration
 * window — the registration badge already conveys "accepting / opening soon /
 * closed". For these we suppress the redundant phase badge so the row shows ONE
 * status (the TP convention). Any OTHER phase (completed / in_progress / archived
 * / …) adds distinct info — "over" / "happening now" / "archived" — and IS kept.
 * Driven off the backend `lifecycle.current_phase` TOKEN, never the human label.
 */
const REGISTRATION_DERIVED_PHASES = new Set<string>([
  "published",
  "registration_open",
  "registration_closed",
]);

/**
 * One dense, scannable MUI result row: a primary name over one secondary line
 * carrying the facility, date range, and commerce facts, with lifecycle and
 * registration state represented by compact MUI Chips.
 *
 * Every label is BrowseTournamentResponse-backed: the phase + registration state
 * come from the backend's authoritative `lifecycle` summary (no client date
 * math). When `lifecycle` is absent we show a neutral honest placeholder — never
 * a fabricated "Accepting entries". The whole row LINKS to the detail
 * (/tournaments/{id}); the uuid is the link target, never visible body text.
 */
function TournamentRow({
  tournament,
  facilityFilter,
  measurementSystem,
}: {
  tournament: BrowseTournamentResponse;
  facilityFilter?: string;
  measurementSystem: "metric" | "imperial";
}) {
  const facilityLine = formatTournamentFacilityLine(
    tournament.facility_name,
    tournament.facility_city,
    tournament.facility_state,
  );
  const dateRange = formatTournamentDateRange(
    tournament.start_date,
    tournament.end_date,
  );
  // Both the lifecycle phase indicator AND the registration state come from the
  // backend's authoritative `lifecycle` summary — no client-side date math.
  const lifecycle = tournament.lifecycle;
  const phase = lifecycle?.current_phase;
  const phaseLabel = lifecycle?.current_phase_label;
  const registrationState = lifecycle?.registration_state;
  const registrationLabel = registrationState
    ? (REGISTRATION_STATE_CARD_LABELS[registrationState] ??
      REGISTRATION_STATE_FALLBACK)
    : REGISTRATION_STATE_FALLBACK;
  // Show the phase badge ONLY when it adds info the registration badge doesn't:
  // suppress it for the registration-derived phases (it would just restate the
  // window), keep it for completed / in_progress / archived / etc. The
  // registration badge always renders — so a row is never badge-less.
  const showPhaseBadge =
    !!phaseLabel && !!phase && !REGISTRATION_DERIVED_PHASES.has(phase);
  const commerceLabel = getTournamentCommerceLabel(
    tournament.registration_commerce,
  );
  const distanceLabel = formatDistance(
    tournament.distance_km,
    measurementSystem,
    " away",
  );

  // No aria-label on the row Link — an explicit aria-label would REPLACE the
  // link's text in the accessibility tree, hiding the facility / dates /
  // registration state / fee from screen-reader users. With it removed the
  // accessible name computes from the full row text, so AT hears the same facts a
  // sighted user reads.
  return (
    <ListItem disablePadding divider>
      <ListItemButton
        component={RouterLink}
        to={`/tournaments/${tournament.tournament_id}${facilityFilter ? `?${new URLSearchParams({ facility_id: facilityFilter })}` : ""}`}
        sx={{
          p: { xs: 1.25, sm: 1.5 },
          gap: { xs: 1.5, sm: 2 },
          alignItems: "stretch",
        }}
      >
        <Box
          sx={(theme) => ({
            width: { xs: 92, sm: 148 },
            minWidth: { xs: 92, sm: 148 },
            height: { xs: 92, sm: 104 },
            borderRadius: 2,
            overflow: "hidden",
            bgcolor: "action.hover",
            background: tournament.poster_image_url
              ? undefined
              : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.22)}, ${alpha(theme.palette.success.main, 0.48)})`,
            display: "grid",
            placeItems: "center",
            alignSelf: "flex-start",
          })}
        >
          {tournament.poster_image_url ? (
            <Box
              component="img"
              src={tournament.poster_image_url}
              alt=""
              loading="lazy"
              sx={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ fontWeight: 800, letterSpacing: 1.2 }}
            >
              Tournament
            </Typography>
          )}
        </Box>
        <Stack spacing={0.75} sx={{ minWidth: 0, flex: 1 }}>
          <ListItemText
            primary={tournament.name}
            secondary={`${facilityLine} · ${dateRange}`}
            sx={{ m: 0, minWidth: 0 }}
            slotProps={{
              primary: {
                sx: {
                  fontWeight: 800,
                  fontSize: { xs: "1rem", sm: "1.08rem" },
                  overflowWrap: "anywhere",
                },
              },
              secondary: { sx: { mt: 0.25, overflowWrap: "anywhere" } },
            }}
          />
          <Typography variant="body2" color="text.secondary">
            {[distanceLabel, commerceLabel].filter(Boolean).join(" · ")}
          </Typography>
          <Stack
            direction="row"
            spacing={0.75}
            useFlexGap
            sx={{ alignItems: "center", flexWrap: "wrap" }}
          >
            {tournament.is_official ? <OfficialBadge /> : null}
            <Chip
              role="status"
              aria-label={registrationLabel}
              label={registrationLabel}
              size="small"
              color="primary"
            />
            {showPhaseBadge ? (
              <Chip
                role="status"
                aria-label={phaseLabel}
                label={phaseLabel}
                size="small"
              />
            ) : null}
            <Chip
              label={
                tournament.tournament_type === "team"
                  ? "Team tournament"
                  : "Individual & doubles"
              }
              size="small"
              variant="outlined"
            />
          </Stack>
        </Stack>
      </ListItemButton>
    </ListItem>
  );
}
